//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import { PojoMap } from 'pojo-maps';
import { PojoSet } from 'pojo-sets';

import { IContentLibraryItem } from '@apis/content-library/contentLibraryApiModel';
import { IGallery, ISite } from '@apis/galleries/galleriesApiModels';
import { IAliasedDateRange } from '@components/date-range-picker/dateRangePickerModels';
import {
  CopyCustomizationSections,
  GALLERIES_TOAST_TYPES,
} from '@galleries/constants/galleriesConstants';
import { IAutoApprovalConfiguration } from '@galleries/models/AutoApprovalConfiguration';
import GalleryContainerId from '@galleries/models/GalleryContainerId';
import Cuid from '@models/Cuid';
import { IRightsRequestState } from '@models/IItemMetadata';
import { IMediaOverlayMetadata } from '@models/IMediaOverlayMetadata';
import { IMlsBeautifiedImageMetadata } from '@models/IMlsMediaItem';
import { INetworkItem } from '@models/INetworkItem';
import ItemId from '@models/ItemId';
import PartialCuid from '@models/PartialCuid';
import ReelId from '@models/ReelId';
import SiteName from '@models/SiteName';
import { Normalized } from '@utils/normalize';

import { ISelectedItem } from 'app/bulk-actions/model/ISelectedItem';

export enum GALLERY_ITEM_STATUS_VALUES {
  ACTION_REQUIRED = 'actionRequired',
  RIGHTS_PENDING = 'rightsPending',
  PUBLISHED = 'published',
  PUBLISHING = 'publishing',
  UPDATING = 'updating',

  // Used only in BV network syndication to represent that an item has products and has been shared
  DISTRIBUTED = 'distributed',
}

export enum SOURCE_FILTER_VALUES {
  ALL_MEDIA = 'All',
  PRODUCTS_NEEDED = 'NeedsProducts',
  PUBLISHED = 'Published',
  RIGHTS_NEEDED = 'NeedsRights',
  RIGHTS_PENDING = 'Pending',
  RIGHTS_REJECTED = 'Expired',
  FAILED_MEDIA = 'FailedToSend',
  PINNED = 'Pinned',
  // TODO DEV-40573: Implement this filter in the getGalleryItems gateway endpoint
  // RIGHTS_PENDING = 'RightsPending',

  // Used only in BV network syndication to represent that an item has products and has been shared
  DISTRIBUTED = 'Distributed',
}

export const GalleriesSourceFilterLabels = {
  [SOURCE_FILTER_VALUES.ALL_MEDIA]: 'All Media',
  [SOURCE_FILTER_VALUES.PRODUCTS_NEEDED]: 'Products Needed',
  [SOURCE_FILTER_VALUES.PUBLISHED]: 'Published',
  [SOURCE_FILTER_VALUES.RIGHTS_NEEDED]: 'Rights Needed',
  [SOURCE_FILTER_VALUES.RIGHTS_PENDING]: 'Rights Pending',
  [SOURCE_FILTER_VALUES.RIGHTS_REJECTED]: 'Rights Rejected',
  [SOURCE_FILTER_VALUES.FAILED_MEDIA]: 'Failed Media',
  [SOURCE_FILTER_VALUES.PINNED]: 'Pinned',
  [SOURCE_FILTER_VALUES.DISTRIBUTED]: 'Distributed',
};

export const VALID_BAZAARVOICE_NETWORK_SOURCES = [
  SOURCE_FILTER_VALUES.ALL_MEDIA,
  SOURCE_FILTER_VALUES.PRODUCTS_NEEDED,
  SOURCE_FILTER_VALUES.RIGHTS_NEEDED,
  SOURCE_FILTER_VALUES.DISTRIBUTED,
];

export interface IGalleryItemMetadata {
  addedToGalleryTimestamp: number;
  isRightsPending: boolean;
  needsProducts: boolean;
  needsRights: boolean;
  isPinned: boolean;
  pinnedTimestamp?: number;
  itemStatus: GALLERY_ITEM_STATUS_VALUES;
  isTermsInCuidRightsRequestMessage?: boolean;
  isBrandOwned?: boolean;
}

export interface IGalleryItem {
  cuid: Cuid;
  galleryId: GalleryContainerId;
  networkItem: INetworkItem;
  galleryItemMetadata: IGalleryItemMetadata;
  mediaOverlayMetadata: IMediaOverlayMetadata;
  beautifiedImageMetadata?: IMlsBeautifiedImageMetadata;
}

export interface IGalleryItems {
  totalItemCount: number;
  pinnedItemsOrderedIds: Cuid[];
  byId: PojoMap<ItemId, IGalleryItem>;
  orderedIds: ItemId[];
}

export interface IGalleriesFlags {
  isLoadingInitialGalleryData: boolean;
  isFetching: boolean;
  isFetchingByCuid: boolean;
  isFetchingFiltered: boolean;
  isFetchingNext: boolean;
  isLastFetch: boolean;
  isFetchingGalleriesList: boolean;
  isFetchingSitesList: boolean;
  isFetchingAutoApprovalConfigs: boolean;
  isFetchingRightsNeededCounts: boolean;
  isSkippingRightsOnItems: PojoSet<ReelId>;
}

/**
 * An absent optional selectedReelAutoApprovalConfig indicates that there is
 * no selected gallery or that the selected gallery doesn't have an auto approval config
 */
export interface IGalleriesData {
  galleries: Normalized<IGallery, GalleryContainerId>;
  sites: ISite[];
  flags: IGalleriesFlags;
  items: IGalleryItems;
  siteName?: SiteName;
  currentlyVisibleToastTypes: PojoSet<GALLERIES_TOAST_TYPES>;
  selectedReelAutoApprovalConfig?: IAutoApprovalConfiguration;
  rightsNeededItemCounts: PojoMap<ReelId, number>;
}

export interface IGalleriesActionBar {
  searchFilterValue: string;
  dateRange: IAliasedDateRange;
  sourceFilterValue: SOURCE_FILTER_VALUES;
}

export interface IGalleriesPicker {
  selectedGalleryId: GalleryContainerId;
}

export interface IGalleriesAddToGalleriesModal {
  isOpen: boolean;
  initiallySelectedItems: ISelectedItem[];
  gallerySelection: PojoSet<ReelId>;
  isBazaarvoiceSyndicationSelected: boolean;
  materializedItems: IContentLibraryItem[];
  currentlySelectedItems: PojoMap<ItemId, IContentLibraryItem>;
  rightsRequestStates: PojoMap<PartialCuid, IRightsRequestState>;
  isLoading: boolean;
}

export interface IGalleriesCreationErrors {
  createGalleryError?: string;
  galleryNameError?: string;
  galleryTypeError?: string;
}

export interface IGalleriesCreationModal {
  autoApprovalStrategy: InstaShopAutoApprovalSources;
  copyCustomizationMode: CopyCustomizationMode;
  errors: IGalleriesCreationErrors;
  galleryLocation: GALLERY_LOCATION;
  galleryName: string;
  isAutoApprovalEnabled: boolean;
  isCreateGalleryModalOpen: boolean;
  isCreatingGallery: boolean;
  isNewGalleryModalOpen: boolean;
  selectedCustomizationSections: CopyCustomizationSections;
  sourceGalleryContainerId?: string;
  typeOfGallery: GALLERY_TYPE;
}

export const CopyCustomizationModes = {
  DEFAULT: 'default',
  COPY: 'copy',
} as const;

export type CopyCustomizationMode =
  (typeof CopyCustomizationModes)[keyof typeof CopyCustomizationModes];

export interface IGalleriesDeleteGalleryErrors {
  deleteGalleryError: string;
}

export interface IGalleriesDeleteGalleryModal {
  deleteGalleryName: string;
  isDeletingGallery: boolean;
  isDeleteGalleryModalOpen: boolean;
  errors: IGalleriesDeleteGalleryErrors;
}

export interface IGalleriesUpdateGalleryNameErrors {
  updateGalleryNameError: string;
}

export interface IGalleriesUpdateGalleryName {
  isUpdatingGalleryName: boolean;
  errors: IGalleriesUpdateGalleryNameErrors;
  galleryNameDraft: string;
}

export interface IGalleriesCustomizationModal {
  isModalOpen: boolean;
  isLocalizationAlertHidden: boolean;
  showValidationStatus: boolean;
}

export interface IGalleriesEmbedModal {
  isModalOpen: boolean;
}

export interface IGalleriesMediaAutofillModal {
  isOpen: boolean;
  readyToShow: boolean;
  modalContent: string;
}

export interface IUnmoderatedContentModal {
  isOpen: boolean;
}

export enum InstaShopAutoApprovalSources {
  allMedia = 'accountFeedMedia',
  productTaggedMedia = 'accountFeedMediaWithProducts',
}

export enum GALLERY_TYPE {
  INSTASHOP = 'instashop',
  CURATED_GALLERY = 'curatedGallery',
}

export enum GALLERY_LOCATION {
  HOMEPAGE = 'homepage',
  PRODUCT_PAGE = 'product',
  CATEGORY_PAGE = 'category',
  OTHER_PAGE = 'other',
}
